/**
 * AdminPage.tsx — the admin experience as a whole PAGE (T-649, ruling A).
 *
 * Replaces AdminPanelDialog: same four sections, same section components reused verbatim
 * (ScoringConfigEditor with its T-383 preview-beside-actions, UserManagementTab, ConfigProvenance),
 * but as a top-level view instead of a modal. The tab bar becomes a persistent side nav; the active
 * section rides the URL (#admin/<section>, owned by useAppView) so each section is deep-linkable.
 *
 * Why a page, not a dialog (T-368 REPORT §8): a modal is dismissible by Escape / click-outside —
 * the wrong affordance for the sign-off that changes what every user's score is computed from (§8d).
 * A page also gives the Model section full width so its knobs and preview read side-by-side (§8b).
 * The move is presentation only: server RBAC (requireRole('admins')) remains the real boundary; a
 * non-admin never reaches this component (AuthedApp gates it on user.isAdmin).
 */
import type { AdminSection } from '../lib/adminHash'
import { ScoringConfigEditor } from '../components/admin/ScoringConfigEditor'
import { UserManagementTab } from '../components/admin/UserManagementTab'
import { ConfigProvenance } from '../components/admin/ConfigProvenance'

interface AdminPageProps {
  /** Active section — carried in the URL by useAppView. */
  section: AdminSection
  /** Select a different section (URL updates; no new Back entry — see useAppView.selectSection). */
  onSelectSection: (section: AdminSection) => void
  /** Leave the admin page for the map. */
  onBackToMap: () => void
}

const NAV: Array<{ id: AdminSection; label: string }> = [
  { id: 'model', label: 'Model parameters' },
  { id: 'users', label: 'Users' },
  { id: 'operator', label: 'Operator settings' },
  { id: 'history', label: 'History' },
]

export function AdminPage({ section, onSelectSection, onBackToMap }: AdminPageProps) {
  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="flex h-12 flex-shrink-0 items-center gap-3 border-b border-border px-4">
        <button
          type="button"
          onClick={onBackToMap}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground
                     hover:bg-accent hover:text-foreground focus-visible:outline-none
                     focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span aria-hidden="true">←</span> Back to map
        </button>
        <h1 className="text-sm font-semibold">Admin</h1>
        <p className="ml-auto hidden text-xs text-muted-foreground sm:block">
          Scoring changes are versioned and require sign-off before they go live.
        </p>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <nav
          aria-label="Admin sections"
          className="flex w-48 flex-shrink-0 flex-col gap-1 border-r border-border p-2"
        >
          {NAV.map((item) => {
            const active = item.id === section
            return (
              <button
                key={item.id}
                type="button"
                aria-current={active ? 'page' : undefined}
                onClick={() => onSelectSection(item.id)}
                className={`rounded-md px-3 py-2 text-left text-sm ${
                  active
                    ? 'bg-accent font-medium text-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
              >
                {item.label}
              </button>
            )
          })}
        </nav>

        <main className="oy-scroll flex-1 overflow-y-auto p-4">
          {section === 'model' ? <ScoringConfigEditor /> : null}
          {section === 'users' ? <UserManagementTab /> : null}
          {section === 'operator' ? (
            <div className="max-w-2xl space-y-2 text-sm text-foreground">
              <p>
                Operator settings that need no sign-off will appear here. Nothing lives here yet:
                every value the panel currently exposes is a scoring parameter, and those are
                governed on the Model parameters section.
              </p>
              <p className="text-muted-foreground">
                The residential-context (R) term stays disabled until block-group data lands.
              </p>
            </div>
          ) : null}
          {section === 'history' ? <ConfigProvenance /> : null}
        </main>
      </div>
    </div>
  )
}
